import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  applyBatchMetadata,
  applyPhotoMetadata as applyPhotoMetadataRequest,
  undoPhotoMetadata as undoPhotoMetadataRequest,
  updatePhotoMetadata
} from '../services/photoMetadataService';

const usePhotoMetadata = ({
  jobId,
  job,
  setJob,
  providerName,
  selectedPhotos,
  clearSelection,
  ensurePolling,
  isPolling,
  setBatchApplying
}) => {
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingState, setSavingState] = useState({});
  const [applyingState, setApplyingState] = useState({});
  const [copiedStates, setCopiedStates] = useState({});
  const [removingTags, setRemovingTags] = useState(new Set());

  const copyToClipboard = useCallback(async (text, field, photoId) => {
    try {
      await navigator.clipboard.writeText(text);
      const key = `${photoId}-${field}`;
      setCopiedStates((previous) => ({ ...previous, [key]: true }));
      setTimeout(() => {
        setCopiedStates((previous) => ({ ...previous, [key]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  }, []);

  const startEditing = useCallback((photoId, field, currentValue) => {
    setEditingField({ photoId, field });
    setEditingValue(currentValue || '');
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingField(null);
    setEditingValue('');
  }, []);

  const updateResult = useCallback((photoId, updater) => {
    setJob((previousJob) => {
      if (!previousJob?.results) {
        return previousJob;
      }

      return {
        ...previousJob,
        results: previousJob.results.map((result) =>
          result.photoId === photoId ? updater(result) : result
        )
      };
    });
  }, [setJob]);

  const saveEdit = useCallback(async () => {
    if (!editingField) {
      return;
    }

    const { photoId, field } = editingField;
    const saveKey = `${photoId}-${field}`;

    try {
      setSavingState((previous) => ({ ...previous, [saveKey]: true }));

      const updatePayload = {};
      if (field === 'title') {
        updatePayload.title = editingValue;
      } else if (field === 'description') {
        updatePayload.description = editingValue;
      } else if (field === 'tags') {
        updatePayload.tags = editingValue;
      }

      await updatePhotoMetadata(jobId, photoId, updatePayload);

      updateResult(photoId, (result) => {
        if (field === 'title') {
          return { ...result, generatedTitle: editingValue };
        }
        if (field === 'description') {
          return { ...result, generatedDescription: editingValue };
        }
        if (field === 'tags') {
          const tagsArray = editingValue
            .split(',')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0);
          return { ...result, generatedTags: tagsArray };
        }
        return result;
      });

      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully`);
      cancelEditing();
    } catch (error) {
      toast.error(`Failed to update ${field}`);
    } finally {
      setSavingState((previous) => ({ ...previous, [saveKey]: false }));
    }
  }, [cancelEditing, editingField, editingValue, jobId, updateResult]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      saveEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
    }
  }, [cancelEditing, saveEdit]);

  const removeTag = useCallback(async (photoId, tagToRemove) => {
    const result = job?.results?.find((item) => item.photoId === photoId);
    if (!result?.generatedTags) {
      return;
    }

    if (tagToRemove.toLowerCase() === 'lenscribe') {
      toast.warning('The "lenscribe" tag cannot be removed');
      return;
    }

    const tagKey = `${photoId}-${tagToRemove}`;
    const updatedTags = result.generatedTags.filter((tag) => tag !== tagToRemove);
    const saveKey = `${photoId}-tags`;

    try {
      setRemovingTags((previous) => new Set([...previous, tagKey]));
      setSavingState((previous) => ({ ...previous, [saveKey]: true }));

      await updatePhotoMetadata(jobId, photoId, { tags: updatedTags.join(', ') });

      updateResult(photoId, (current) => ({
        ...current,
        generatedTags: updatedTags
      }));

      toast.success('Tag removed successfully');
    } catch (error) {
      toast.error('Failed to remove tag');
    } finally {
      setRemovingTags((previous) => {
        const next = new Set(previous);
        next.delete(tagKey);
        return next;
      });
      setSavingState((previous) => ({ ...previous, [saveKey]: false }));
    }
  }, [job, jobId, updateResult]);

  const restoreOriginal = useCallback(async (photoId, field, originalValue) => {
    if (!originalValue) {
      return;
    }

    const saveKey = `${photoId}-${field}`;

    try {
      setSavingState((previous) => ({ ...previous, [saveKey]: true }));

      const updatePayload = {};
      if (field === 'title') {
        updatePayload.title = originalValue;
      } else if (field === 'description') {
        updatePayload.description = originalValue;
      } else if (field === 'tags') {
        updatePayload.tags = originalValue;
      }

      await updatePhotoMetadata(jobId, photoId, updatePayload);

      updateResult(photoId, (result) => {
        if (field === 'title') {
          return { ...result, generatedTitle: originalValue };
        }
        if (field === 'description') {
          return { ...result, generatedDescription: originalValue };
        }
        if (field === 'tags') {
          const tagsArray = originalValue
            .split(',')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0);
          return { ...result, generatedTags: tagsArray };
        }
        return result;
      });

      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} restored to original`);
    } catch (error) {
      toast.error(`Failed to restore original ${field}`);
    } finally {
      setSavingState((previous) => ({ ...previous, [saveKey]: false }));
    }
  }, [jobId, updateResult]);

  const applyPhotoMetadata = useCallback(async (photoId) => {
    try {
      setApplyingState((previous) => ({ ...previous, [photoId]: true }));

      await applyPhotoMetadataRequest(jobId, photoId);

      updateResult(photoId, (result) => ({
        ...result,
        applied: true,
        appliedAt: new Date()
      }));

      toast.success(`Metadata applied to ${providerName} successfully`);
    } catch (error) {
      toast.error(`Failed to apply metadata to ${providerName}`);
    } finally {
      setApplyingState((previous) => ({ ...previous, [photoId]: false }));
    }
  }, [jobId, providerName, updateResult]);

  const undoApply = useCallback(async (photoId) => {
    try {
      setApplyingState((previous) => ({ ...previous, [photoId]: true }));

      await undoPhotoMetadataRequest(jobId, photoId);

      updateResult(photoId, (result) => ({
        ...result,
        applied: false,
        undoneAt: new Date(),
        backupTitle: undefined,
        backupDescription: undefined,
        backupTags: undefined,
        appliedAt: undefined
      }));

      toast.success(`Original metadata restored to ${providerName}`);
    } catch (error) {
      toast.error('Failed to undo metadata changes');
    } finally {
      setApplyingState((previous) => ({ ...previous, [photoId]: false }));
    }
  }, [jobId, providerName, updateResult]);

  const batchApplyMetadata = useCallback(
    async (photoIdsOverride = null) => {
      const photoIds = photoIdsOverride || Array.from(selectedPhotos);

      if (photoIds.length === 0) {
        toast.warning(`Please select photos to apply to ${providerName}`);
        return;
      }

      setBatchApplying(true);

      try {
        const response = await applyBatchMetadata(jobId, photoIds);
        toast.success(`Started applying metadata to ${response.totalPhotos} photos in the background`);

        clearSelection();

        if (!isPolling) {
          ensurePolling();
        }
      } catch (error) {
        if (error.response?.status === 409) {
          toast.warning('Batch apply already in progress for this job');
        } else if (error.response?.status === 400) {
          const errorMessage = error.response?.data?.error || 'Invalid request';
          toast.error(`Failed to start apply: ${errorMessage}`);
        } else {
          toast.error('Failed to start batch apply operation');
        }
        setBatchApplying(false);
      }
    },
    [
      clearSelection,
      ensurePolling,
      isPolling,
      jobId,
      providerName,
      selectedPhotos,
      setBatchApplying
    ]
  );

  return {
    editingField,
    editingValue,
    setEditingValue,
    savingState,
    applyingState,
    copiedStates,
    removingTags,
    copyToClipboard,
    startEditing,
    cancelEditing,
    saveEdit,
    handleKeyPress,
    restoreOriginal,
    removeTag,
    applyPhotoMetadata,
    undoApply,
    batchApplyMetadata
  };
};

export default usePhotoMetadata;
